
import React, { memo } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LazyImage } from "@/components/ui/lazy-image";
import { Home, Building2, Construction, Sparkles, Calendar, ArrowRight, Star } from "lucide-react";
import regularCleaningImage from "@/assets/regular-living-room.jpg";
import officeImage from "@/assets/office-cleaning.jpg";
import deepCleaningImage from "@/assets/realistic-bathroom-clean.jpg";
import postConstructionImage from "@/assets/post-construction.jpg";
import movingCleaningImage from "@/assets/move-out-living-boxes.jpg";
import windowCleaningImage from "@/assets/window-cleaning-hero.jpg";

const ServicesSection = memo(() => {
const services = [
    {
      icon: Sparkles,
      title: "Deep Cleaning",
      description: "The essential starting point for every home. Prepare your space for the Routine Cleaners standard with our comprehensive deep cleaning service.",
      image: deepCleaningImage,
      features: ["Required before regular service", "Removes all accumulated dirt", "Sets Routine Cleaners standard", "Perfect for new clients"],
      link: "/services/deep-cleaning"
    },
    {
      icon: Home,
      title: "Regular House Cleaning",
      description: "Maintain your home at the Routine Cleaners standard with weekly, bi-weekly, or monthly service. Bi-weekly offers the best value.",
      image: regularCleaningImage,
      features: ["Weekly, bi-weekly, monthly", "Bi-weekly most popular", "Maintains deep clean standard", "All supplies included"],
      link: "/services/regular-cleaning",
      highlight: true
    },
    {
      icon: Calendar,
      title: "Moving Cleaning",
      description: "Complete move-in/move-out cleaning including inside cabinets, appliances, and every corner of your new or former home.",
      image: movingCleaningImage,
      features: ["Inside all cabinets & drawers", "Complete appliance cleaning", "Perfect for new homes", "Move-in ready guarantee"],
      link: "/services/moving-cleaning"
    },
    {
      icon: Construction,
      title: "Post-Construction Cleaning",
      description: "All-inclusive residential cleaning after construction projects. Remove dust, debris, and get your new space move-in ready.",
      image: postConstructionImage,
      features: ["All-inclusive service", "Construction debris removal", "Dust elimination", "Move-in ready"],
      link: "/services/post-construction-cleaning"
    },
    {
      icon: Sparkles,
      title: "Window Cleaning",
      description: "Professional residential window cleaning for accessible windows inside your home with streak-free results.",
      image: windowCleaningImage,
      features: ["Interior accessible windows", "Safe cleaning methods", "Streak-free finish", "Regular maintenance"],
      link: "/services/window-cleaning"
    },
    {
      icon: Building2,
      title: "Office Cleaning",
      description: "Professional commercial cleaning for offices, with both deep cleaning and regular maintenance options available.",
      image: officeImage,
      features: ["After-hours service", "Deep & regular cleaning", "Professional staff", "Flexible contracts"],
      link: "/services/office-cleaning"
    }
  ];

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-background to-muted">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Our <span className="bg-gradient-to-r from-brand-gold-dark to-brand-gold-light bg-clip-text text-transparent">Cleaning Services</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From regular home maintenance to specialized commercial cleaning, 
            we provide comprehensive cleaning solutions across Connecticut.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card key={index} className={`service-card group hover:shadow-xl transition-all duration-300 border-border hover:border-accent/20 ${service.highlight ? 'ring-2 ring-accent/30 bg-gradient-to-br from-background to-accent/5' : ''}`}>
                <div className="relative overflow-hidden rounded-t-lg">
                  <LazyImage
                    src={service.image}
                    alt={`${service.title} - Professional cleaning service in Connecticut`}
                    width={400}
                    height={192}
                    loading={index < 3 ? "eager" : "lazy"}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px"
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className={`absolute top-4 left-4 p-2 rounded-lg shadow-lg ${service.highlight ? 'bg-gradient-to-r from-brand-gold-dark to-brand-gold-light' : 'bg-accent'}`}>
                    <IconComponent className={`h-6 w-6 ${service.highlight ? 'text-white' : 'text-accent-foreground'}`} />
                  </div>
                  {service.highlight && (
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-brand-gold-dark to-brand-gold-light text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                      <Star className="h-4 w-4 mr-1" />
                      Most Popular
                    </div>
                  )}
                </div>
                
                <CardHeader>
                  <CardTitle className="text-foreground">{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                
                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-muted-foreground">
                        <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3 flex-shrink-0"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button variant="outline-gold" className="w-full group" asChild>
                    <a href={service.link} className="flex items-center justify-center gap-2">
                      <span className="md:hidden">Learn More</span>
                      <span className="hidden md:inline">Learn More About {service.title}</span>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center bg-primary rounded-2xl p-6 sm:p-8 lg:p-12 mx-2 sm:mx-0">
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary-foreground mb-4">
            Ready to Experience the Routine Cleaners Difference?
          </h3>
          <p className="text-primary-foreground/90 mb-6 max-w-2xl mx-auto text-sm sm:text-base">
            Join hundreds of satisfied customers across Connecticut who trust us with their cleaning needs.
          </p>
          <div className="px-2 sm:px-0">
            <Button 
              variant="hero" 
              size="xl" 
              className="w-full sm:w-auto px-6 sm:px-8 touch-manipulation" 
              asChild
            >
              <a href="/estimate">Get Your Free Estimate Today</a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
});

ServicesSection.displayName = 'ServicesSection';

export default ServicesSection;
